import { useState } from 'react'
import type { EliteOnboardingStatusResponse } from '../../../types/misc.types'
import type { PricingDetailsContent } from '../types/pricing.types'
import {
  getEliteCurrentPlanTenure,
  getEliteFocusEligiblePlan,
  getElitePlanStatusLabel,
  getElitePlanTenureLabel,
  getEliteSubscriptionPlanPriceDisplayExGst
} from '../../../components/elite/dashboard/eliteOnboardingFocusPlan'
import { getEliteSubscriptionValidityDateLabel } from '../../../components/elite/dashboard/eliteMembershipRenewalWindow'
import { convertEpochToDate, roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { ChevronRightIcon } from '../icons/ChevronRightIcon'
import {
  ElitePlansPricingDetailsBottomSheet,
  elitePlansPricingDetailsTitleId
} from './ElitePlansPricingDetailsBottomSheet'
import VaulDrawer from '../../../components/BottomSheetComponents/VaulDrawer'
import { ElitePlansEventId, trackElitePlansClick } from '../analytics'

type ElitePlansMembershipPlanCardProps = {
  eliteOnboardingStatus?: EliteOnboardingStatusResponse
  pricingDetailsTemplate?: PricingDetailsContent
  planNamePrefix?: string
  className?: string
}

export function ElitePlansMembershipPlanCard({
  eliteOnboardingStatus,
  pricingDetailsTemplate,
  planNamePrefix = 'Prime',
  className = 'my-10 mx-5'
}: ElitePlansMembershipPlanCardProps) {
  const [isPricingDetailsOpen, setIsPricingDetailsOpen] = useState(false)
  const activePlan = getEliteFocusEligiblePlan(eliteOnboardingStatus)
  const { period: planTenurePeriod, value: planTenureValue } =
    getEliteCurrentPlanTenure(eliteOnboardingStatus)
  const planTenureLabel = getElitePlanTenureLabel({
    period: planTenurePeriod,
    value: planTenureValue
  })
  const planTypeLabel = `${planNamePrefix} ${planTenureLabel}`
  const planDurationSuffix = planTenureLabel === 'Yearly' ? '/yr' : '/mo'
  const planStatusLabel = getElitePlanStatusLabel(eliteOnboardingStatus)
  const priceDisplay = getEliteSubscriptionPlanPriceDisplayExGst(
    eliteOnboardingStatus?.subscriptionDetails,
    activePlan
  )
  const primaryPlanFees = priceDisplay.primaryExGst
  const listStrikeAmount = priceDisplay.listStrikeExGst
  const showStrikeThrough = priceDisplay.showStrikeThrough
  const subscriptionExpiresOn = eliteOnboardingStatus?.subscriptionDetails?.subscriptionExpiresOn

  const openPlanDetails = () => {
    trackElitePlansClick(ElitePlansEventId.ViewDetails, {
      planId: 'elite-prime',
      buttonLabel: 'Plan details'
    })
    if (pricingDetailsTemplate) {
      setIsPricingDetailsOpen(true)
    }
  }

  return (
    <>
      <div className={`overflow-hidden rounded-2xl bg-white ${className}`}>
        <div className='flex items-start justify-between gap-3 px-5 py-4'>
          <div>
            <p className='text-sm font-medium text-[#65321D]'>{planTypeLabel}</p>
            <p className='mt-1 text-[13px] font-medium text-[#BB4F22] text-left'>{planStatusLabel}</p>
          </div>
          <div className='text-right'>
            <p className='text-sm font-medium text-[#232323]'>
              {roundOffAndFormatNumberWithCurrency(primaryPlanFees)}
              {planDurationSuffix}
              <span className='text-[12px] font-normal text-[#8E9393]'>+GST</span>
            </p>
            {showStrikeThrough ? (
              <p className='text-[13px] text-[#8E9393] line-through'>
                {roundOffAndFormatNumberWithCurrency(listStrikeAmount)}
                {planDurationSuffix}
              </p>
            ) : null}
          </div>
        </div>
        <hr className='border-[#E6E6E6]' />
        <p className='px-5 py-4 text-left text-[13px] text-[#65321D]'>
          {getEliteSubscriptionValidityDateLabel(eliteOnboardingStatus)}{' '}
          {subscriptionExpiresOn ? convertEpochToDate(subscriptionExpiresOn, '%d %b %Y') : 'N/A'}
        </p>
        {/* <hr className='border-[#E6E6E6]' />
        <button
          type='button'
          className='ep-focus-ring flex min-h-12 w-full items-center justify-between gap-3 border-none bg-transparent px-5 py-4 text-left text-[13px] font-medium text-[#65321D]'
          onClick={openPlanDetails}
        >
          <span>Plan details</span>
          <ChevronRightIcon size={10} color='#65321D' className='shrink-0 opacity-70' />
        </button> */}
      </div>

      {pricingDetailsTemplate ? (
        <VaulDrawer
          open={isPricingDetailsOpen}
          setOpen={setIsPricingDetailsOpen}
          titleId={elitePlansPricingDetailsTitleId}
        >
          <ElitePlansPricingDetailsBottomSheet
            open={isPricingDetailsOpen}
            template={pricingDetailsTemplate}
            onClose={() => setIsPricingDetailsOpen(false)}
          />
        </VaulDrawer>
      ) : null}
    </>
  )
}
